import axios from "axios";
import Cookies from "js-cookie";

const baseURL = process.env.REACT_APP_BASE_BACKEND_API_URL;

/**
 * Fetch list of screens (for role permissions)
 */
export const getScreens = async () => {
  const token = Cookies.get("accessToken");
  try {
    const response = await axios.get(`${baseURL}api/v1/screens`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const value = response.data?.body?.value;
    return Array.isArray(value) ? value : [];
  } catch (error) {
    console.error("Error fetching screens:", error);
    throw error;
  }
};

/**
 * Fetch a single role by id (for edit)
 * GET api/v1/roles/:id
 */
export const getRoleById = async (roleId) => {
  const token = Cookies.get("accessToken");
  try {
    const response = await axios.get(`${baseURL}api/v1/roles/${roleId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data?.body?.value ?? response.data?.body ?? response.data;
  } catch (error) {
    console.error("Error fetching role by id:", error);
    throw error;
  }
};

/**
 * Fetch list of roles (optional organizationId for super_admin)
 */
export const getRoleList = async (organizationId) => {
  const token = Cookies.get("accessToken");
  let url = `${baseURL}api/v1/roles?`;
  if (organizationId) {
    url += `organizationId=${encodeURIComponent(organizationId)}`;
  }
  try {
    const response = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data?.body?.value ?? response.data;
  } catch (error) {
    console.error("Error fetching roles:", error);
    throw error;
  }
};

/**
 * Create role (super_admin only).
 * Body: { name, description, organizationId, screenPermissions } — organizationId and screenPermissions only for super admin
 * screenPermissions: [{ screenId, canView, canCreate, canUpdate, canDelete }, ...]
 */
export const createRole = async ({ name, description, organizationId, screenPermissions }) => {
  const token = Cookies.get("accessToken");
  try {
    const body = { name, description: description || "", organizationId: organizationId ?? undefined };
    if (Array.isArray(screenPermissions) && screenPermissions.length > 0) {
      body.screenPermissions = screenPermissions;
    }
    const response = await axios.post(
      `${baseURL}api/v1/roles`,
      body,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  } catch (error) {
    console.error("Error creating role:", error);
    throw error;
  }
};

/**
 * Update role by id.
 * Body: { name, description, organizationId, screenPermissions } — organizationId and screenPermissions only for super admin
 * screenPermissions: [{ screenId, canView, canCreate, canUpdate, canDelete }, ...]
 */
export const updateRole = async (roleId, { name, description, organizationId, screenPermissions }) => {
  const token = Cookies.get("accessToken");
  try {
    const body = { name, description: description || "", organizationId: organizationId ?? undefined };
    if (Array.isArray(screenPermissions)) {
      body.screenPermissions = screenPermissions;
    }
    const response = await axios.put(
      `${baseURL}api/v1/roles/${roleId}`,
      body,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  } catch (error) {
    console.error("Error updating role:", error);
    throw error;
  }
};

/**
 * Get role permissions.
 * GET api/v1/roles/:roleId/permissions
 */
export const getRolePermissions = async (roleId) => {
  const token = Cookies.get("accessToken");
  try {
    const response = await axios.get(`${baseURL}api/v1/roles/${roleId}/permissions`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    // Handle response structure: { header: { code: 600 }, body: { value: [...] } }
    if (response.data?.body?.value && Array.isArray(response.data.body.value)) {
      return response.data.body.value;
    }
    const value = response.data?.body?.value ?? response.data?.body ?? response.data;
    return value?.permissions ?? (Array.isArray(value) ? value : []);
  } catch (error) {
    console.error("Error fetching role permissions:", error);
    throw error;
  }
};

/**
 * Update role permissions.
 * PUT api/v1/roles/:roleId/permissions
 * Body: { permissions: [{ screenId, screenCode, screenName, canView, canCreate, canUpdate, canDelete }, ...] }
 */
export const updateRolePermissions = async (roleId, { permissions }) => {
  const token = Cookies.get("accessToken");
  try {
    const response = await axios.put(
      `${baseURL}api/v1/roles/${roleId}/permissions`,
      { permissions },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  } catch (error) {
    console.error("Error updating role permissions:", error);
    throw error;
  }
};

/**
 * Delete role by id
 */
export const deleteRole = async (roleId) => {
  const token = Cookies.get("accessToken");
  try {
    const response = await axios.delete(
      `${baseURL}api/v1/roles/${roleId}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  } catch (error) {
    console.error("Error deleting role:", error);
    throw error;
  }
};
